import {
    IIndicatorFilterResponse,
    JobType,
} from '@kcftech/prometheustypescriptapiclient';
import { Button } from 'components/button/Button';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DocumentTitle } from 'components/DocumentTitle';
import { Select, SelectOption } from 'components/forms/Select';
import { TablePageLayout } from 'components/page/styled';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { usePagedJobsDataSource } from 'custom_hooks/data_hooks/data_source/jobDataSource';
import { usePopup } from 'custom_hooks/usePopup';
import { IndicatorFilterDropdown } from 'pages/Settings/SetupTools/IndicatorFilterDropdown';
import { JobsTable } from 'pages/Settings/SetupTools/JobsTable';
import { SetupToolsTemplateDownloadButton } from 'pages/Settings/SetupTools/SetupToolsTemplateDownloadButton';
import { SetupToolsTemplateUploadButton } from 'pages/Settings/SetupTools/SetupToolsTemplateUploadButton';
import { jobTypeToName } from 'pages/Settings/SetupTools/shared';
import { SearchCreateButtonContainer } from 'pages/Settings/shared/styled';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SecondaryText } from 'styles/theme_styles/text';
import { tokens } from 'styles/tokens';
import useInterval from 'use-interval';
import { ContextualPageProps, Lookup } from 'utils/types';

const tableRefreshMs = 60 * 1000;

const successMessage = "Done! We'll email you when your upload has been processed.";

const UploadMessage = styled.div<{ $isError?: boolean }>`
    color: ${({ $isError }) => ($isError ? tokens.error.default : tokens.action.confirm)};
    display: flex;
    align-items: center;
`;

const ErrorPopupButton = styled(Button)`
    color: ${tokens.text.link};
`;

const DropdownAndButtonsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: start;
    margin: 1.5rem 0rem;
    gap: 1rem;
`;

const jobTypeToDescription: Lookup<JobType, string> = {
    SensorAssignment:
        'Download a template with any existing account information already applied, add your sensor assignments, and re-upload.',
    SystemCreation:
        'Download a template, add new items to be created in the tree, and re-upload.',
    EditAssetDetails:
        'Download a template with any existing account information already applied, edit any asset details, and re-upload.',
    EditIndicators:
        'Download a template with all existing account indicators, then edit the indicators as needed and re-upload.',
    EditGroups: '',
};

/**
 * Select-compatible options for all of the available job types.
 */
const jobTypeOptions: SelectOption[] = Object.values(JobType)
    .map((type) => ({
        title: jobTypeToName[type],
        value: type,
    }))
    .filter((option) => option.value !== JobType.EditGroups);

export function SetupToolsPage({ corporation, location }: ContextualPageProps) {
    const [selectedFilter, setSelectedFilter] = useState<IIndicatorFilterResponse>();

    const [selectedJobType, setSelectedJobType] = useState(JobType.SensorAssignment);

    const [uploadErrors, setUploadErrors] = useState<string[]>([]);
    const [uploadComplete, setUploadComplete] = useState(false);

    const {
        isOpen: errorPopupOpen,
        open: openErrorPopup,
        close: closeErrorPopup,
        triggerRef,
        popupRef,
    } = usePopup();

    const [nameSearch, setNameSearch] = useState('');

    const dataSource = usePagedJobsDataSource({
        name: 'jobs-table',
        defaultFilter: {
            corporationId: corporation?.Id,
            locationId: location?.Id,
            nameSearch: '',
        },
    });

    const { setFilter, reloadPage, isLoading } = dataSource;

    useInterval(reloadPage, isLoading ? false : tableRefreshMs, false);

    const clearUploadState = () => {
        setUploadComplete(false);
        setUploadErrors([]);
    };

    useEffect(() => {
        setFilter((currentFilter) => ({
            ...currentFilter,
            corporationId: corporation?.Id,
            locationId: location?.Id,
            nameSearch,
        }));
    }, [corporation, location, nameSearch, setFilter]);

    useEffect(() => {
        clearUploadState();
        setSelectedFilter(undefined);
    }, [corporation, location, selectedJobType]);

    const onTemplateUpload = (errors: string[] | undefined) => {
        setUploadComplete(true);
        setUploadErrors(
            errors ? errors.map((error, index) => `(${index + 1}) ${error}`) : []
        );

        if (!errors) {
            reloadPage();
        }
    };

    return (
        <TablePageLayout>
            <DocumentTitle
                title={`${
                    location?.Name ?? corporation?.Name ?? ''
                } Setup Tools Settings`}
            />
            {!location && (
                <div className="pt-20 pb-10">
                    Select a location in the top bar to run a setup tools job.
                </div>
            )}
            <DropdownAndButtonsContainer>
                <Select
                    className="mr-10"
                    style={{ width: '20rem' }}
                    options={jobTypeOptions}
                    value={selectedJobType}
                    onChange={(e) => setSelectedJobType(e.target.value as JobType)}
                />
                {selectedJobType === JobType.EditIndicators && (
                    <IndicatorFilterDropdown
                        locationId={location?.Id}
                        onChange={setSelectedFilter}
                        indicatorFilter={selectedFilter}
                    />
                )}
                <div className="flex-row gap-20">
                    <SetupToolsTemplateDownloadButton
                        location={location}
                        filterId={selectedFilter?.Id}
                        jobType={selectedJobType}
                    />
                    <SetupToolsTemplateUploadButton
                        locationId={location?.Id}
                        jobType={selectedJobType}
                        onStart={clearUploadState}
                        onComplete={onTemplateUpload}
                    />
                </div>
                {uploadComplete && (
                    <>
                        {uploadErrors.length === 0 ? (
                            <UploadMessage>{successMessage}</UploadMessage>
                        ) : (
                            <UploadMessage $isError>
                                The uploaded file had validation errors.
                                <ErrorPopupButton
                                    ref={triggerRef}
                                    onClick={openErrorPopup}
                                >
                                    View errors
                                </ErrorPopupButton>
                            </UploadMessage>
                        )}
                        {errorPopupOpen && uploadErrors.length > 0 && (
                            <StatelessPopup
                                onClose={closeErrorPopup}
                                ref={popupRef}
                                anchor={triggerRef.current}
                            >
                                <div className="flex-column">
                                    {uploadErrors.map((error) => (
                                        <span key={error}>{error}</span>
                                    ))}
                                </div>
                            </StatelessPopup>
                        )}
                    </>
                )}
            </DropdownAndButtonsContainer>
            <SecondaryText>{jobTypeToDescription[selectedJobType]}</SecondaryText>
            <SearchCreateButtonContainer>
                <DebouncedSearchInput
                    placeholder="Search"
                    value={nameSearch}
                    onChange={setNameSearch}
                />
            </SearchCreateButtonContainer>
            <JobsTable dataSource={dataSource} />
        </TablePageLayout>
    );
}
